/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");
      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error(
            "<carousel-pager> needs to be a child of <site-carousel>"
          );
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    const config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: true };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, config);
    this.handleSlideChange();
  }

  previous() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    const pagers = Array.from(this.querySelectorAll("carousel-pager"));

    if (this.swiper) {
      this.swiper.on("slideChange", () => {
        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll("button"));
          const nextPager = buttons[this.swiper.realIndex];

          buttons.forEach((button) => button.classList.remove("active"));
          nextPager.classList.add("active");
        });
      });
    }
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.menuTimeout = null;
    this.header = null;
  }

  connectedCallback() {
    this.header = this.querySelector(".site-header");
    this.setupHeaderScroll();
    this.setActive();
  }

  setupHeaderScroll() {
    const header = this.querySelector(".site-header");

    if (!header.classList.contains("active")) {
      window.addEventListener("scroll", () => {
        const scrollY = window.scrollY || window.pageYOffset;

        header.classList.toggle("active", scrollY >= 40);
      });
    }
  }

  setActive() {
    const scrollY = window.scrollY || window.pageYOffset;
    this.header.classList.toggle("active", scrollY >= 40);
  }
}

customElements.define("site-header", SiteHeader);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const form = this.querySelector("form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const idEl = form.querySelector('[name="id"]');
      const qtyEl = form.querySelector('[name="quantity"]');
      const sellingPlanEl = form.querySelector('[name="selling_plan"]');

      if (!idEl) return;

      const qty = qtyEl && qtyEl.value ? Number(qtyEl.value) : 1;
      const sellingPlan =
        sellingPlanEl && sellingPlanEl.value
          ? Number(sellingPlanEl.value)
          : undefined;

      const scope =
        form.closest("[data-upsell-section]") ||
        form.closest("product-form") ||
        form.closest("section") ||
        document;

      const upsellEl = scope.querySelector("[data-upsell-checkbox]");

      const additionalItems = [];
      if (upsellEl && upsellEl.checked && upsellEl.dataset.variantId) {
        additionalItems.push({
          id: Number(upsellEl.dataset.variantId),
          quantity: qty,
        });
      }

      const cart = document.querySelector("site-cart");
      if (!cart) return;

      try {
        await cart.addVariantToCart(
          idEl.value,
          qty,
          sellingPlan,
          true,
          additionalItems
        );

        if (qty === 1) {
          const timerMessageEl = document.querySelector("timer-message");
          if (timerMessageEl) {
            timerMessageEl.showMessage();
            timerMessageEl.initTimer();
          }
        }
      } catch (err) {
        console.error("Cart add failed", err);
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Cart Update
*/

class CartUpdate extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      "change",
      this.debounce(async (e) => {
        const cart = document.querySelector("site-cart");
        const idx = e.target.dataset.index;
        const val = e.target.value;

        if (cart) {
          await cart.updateQuantity(idx, val);
        }
      }, 300)
    );
  }
}

customElements.define("cart-update", CartUpdate);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;

      if (cart) {
        await cart.updateQuantity(idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/*
Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add("active");
      document.body.style.overflow = "hidden";
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove("active");
      document.body.removeAttribute("style");
    }
  }

  toggleCart() {
    const header = document.querySelector("site-header");
    if (this.cart.classList.contains("active")) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  getCartSections() {
    return [
      {
        section: "main-cart-items",
        selector: "#main-cart-items",
      },
      {
        section: "main-cart-footer",
        selector: "#main-cart-footer",
      },
      {
        section: "main-cart-header",
        selector: "#main-cart-header",
      },
      {
        section: "header",
        selector: "#header",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        elToReplace.innerHTML = this.getSectionInnerHTML(
          sections[sectionKey],
          activeSection.selector
        );
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(
    id,
    quantity = 1,
    sellingPlan,
    openCart = true,
    additionalItems = []
  ) {
    const main = sellingPlan
      ? {
          id: Number(id),
          quantity: Number(quantity),
          selling_plan: Number(sellingPlan),
        }
      : { id: Number(id), quantity: Number(quantity) };

    const items = [main, ...additionalItems.filter(Boolean)];

    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (!this.cart.classList.contains("active") && openCart) {
      this.toggleCart();
    }

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);
      })
      .catch((e) => {
        // handle error
      });
  }

  updateQuantity(line, quantity) {
    const items = Array.from(document.querySelectorAll("cart-item"));
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);
    const item = items[line - 1];
    const price = item.querySelector(".cart-price");
    const loader = item.querySelector(".cart-loader");

    if (price) {
      price.classList.add("hidden");
    }
    if (loader) {
      loader.classList.remove("hidden");
    }

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then((response) => {
        if (price) {
          price.classList.remove("hidden");
        }
        if (loader) {
          loader.classList.add("hidden");
        }

        this.renderCartSections(response.sections);
      })
      .catch(() => {
        // handle error
      });
  }

  updateCart(updates, shouldRefresh = true) {
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    return fetch("/cart/update", config)
      .then((response) => response.json())
      .then((response) => {
        if (shouldRefresh) {
          this.renderCartSections(response.sections);

          if (response.items.length == 0) {
            const timerMessageEl = document.querySelector("timer-message");
            timerMessageEl.stopTimer();
            timerMessageEl.hideMessage();
          }
        }
      })
      .catch(() => {
        // handle error
      });
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return await resp.json();
  }
}

customElements.define("site-cart", SiteCart);

/*
Select Input
*/

class SelectInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const select = this.querySelector("select");
    const input = select.closest(".text-input");
    if (select.value && input) {
      input.classList.add("active");
    }
    select.addEventListener("change", (e) => {
      this.setActiveValue(e.target.value);
      if (input) {
        input.classList.add("active");
      }
    });
  }

  setActiveValue(val) {
    const options = Array.from(this.querySelectorAll("option"));
    const activeOption = options.find((o) => o.value === val);
    const target = this.querySelector(".select-target");

    if (activeOption) {
      target.innerHTML = activeOption.innerHTML;
    }
  }
}

customElements.define("select-input", SelectInput);

/*
Accordion Item
*/

class AccordionItem extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const summary = this.querySelector("summary");

    summary.addEventListener("click", (e) => {
      e.preventDefault();
      this.closeExpandedAccordions();
      this.toggle();
    });

    const closeButtons = this.querySelectorAll(".accordion-close");
    closeButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.closeExpandedAccordions();
        this.toggle();
      });
    });

    if (this.dataset.open === "true") {
      this.open(this);
    }
  }

  closeExpandedAccordions() {
    this.closest("section")
      .querySelectorAll("accordion-item")
      .forEach((item) => {
        const details = item.querySelector("details");
        if (details.open) {
          this.close(item);
        }
      });
  }

  toggle() {
    const details = this.querySelector("details");

    if (details.open) {
      this.close(this);
    } else {
      this.open(this);
    }
  }

  close(item) {
    const icon = item.querySelector("svg");
    const details = item.querySelector("details");
    const content = item.querySelector(".content");

    content.style.maxHeight = `${content.scrollHeight}px`;
    content.getBoundingClientRect();
    icon.style.transform = "rotate(90deg)";
    content.style.maxHeight = "0px";
    setTimeout(() => {
      details.open = false;
    }, 300);
  }

  getHeight(el) {
    const clone = el.cloneNode(true);
    clone.style.height = "auto";
    clone.style.visibility = "hidden";
    clone.style.position = "absolute";
    clone.style.top = "-9999px";
    document.body.appendChild(clone);
    const height = clone.scrollHeight;
    document.body.removeChild(clone);
    return height;
  }

  open(item) {
    const icon = item.querySelector("svg");
    const details = item.querySelector("details");
    const content = item.querySelector(".content");

    details.open = true;
    content.style.maxHeight = "none";
    const contentHeight = content.scrollHeight;
    content.style.maxHeight = "0px";
    icon.style.transform = "rotate(45deg)";

    content.getBoundingClientRect();
    content.style.maxHeight = `${contentHeight}px`;
  }
}

customElements.define("accordion-item", AccordionItem);

/*
Quantity Selector
*/

class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.quantity = 1;
  }

  connectedCallback() {
    this.input = this.querySelector("input");
    this.currQuantity = this.querySelector(".curr-quantity");
    this.addEventListener("click", (e) => {
      this.handleClick(e);
    });
  }

  handleClick(e) {
    e.preventDefault();
    const button = e.target.closest("button");

    if (button) {
      if (button.name === "increment") {
        this.quantity = this.quantity + 1;
      } else {
        if (this.quantity > 1) {
          this.quantity = this.quantity - 1;
        }
      }

      this.input.value = this.quantity;
      this.currQuantity.innerText = this.quantity;
      this.closest("product-form").querySelector('[name="quantity"]').value =
        this.quantity;
    }
  }
}

customElements.define("quantity-selector", QuantitySelector);

/*
Cart Item
*/

class CartItem extends HTMLElement {
  connectedCallback() {
    this.quantityInputEl = this.querySelector("number-input input");
    this.removeButtonEl = this.querySelector(".remove-button-js");
    this.selectedVariant = JSON.parse(this.dataset.selectedVariant);

    this.addQuantityChangeEvent();
    this.addRemoveEvent();
  }

  showLoader() {
    const item = this.quantityInputEl.closest("cart-item");

    item
      .querySelector(".cart-item__totals")
      .firstElementChild.classList.add("hidden");
    item
      .querySelector(".cart-item__totals")
      .lastElementChild.classList.remove("hidden");
  }

  addQuantityChangeEvent() {
    this.quantityInputEl.addEventListener("change", () => {
      this.showLoader();

      const siteCart = document.querySelector("site-cart");
      const quantityVal = +this.quantityInputEl.value;
      siteCart.updateCart({ [this.selectedVariant.id]: quantityVal });
    });
  }

  addRemoveEvent() {
    this.removeButtonEl.addEventListener("click", () => {
      this.showLoader();

      const siteCart = document.querySelector("site-cart");
      siteCart.updateCart({ [this.selectedVariant.id]: 0 });
    });
  }
}

customElements.define("cart-item", CartItem);

/*
Number Input
*/

class NumberInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });

    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  onButtonClick(e) {
    e.preventDefault();
    const previousValue = this.input.value;
    const button = e.target.closest("button");

    if (button) {
      button.name === "increment" ? this.input.stepUp() : this.input.stepDown();

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  }
}

customElements.define("number-input", NumberInput);

/*
Site Video
*/

class SiteVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector("video");

    this.video.addEventListener("click", (e) => {
      e.preventDefault();

      this.pauseOtherVideos();

      if (this.video.paused) {
        this.video.play();
        // this.video.controls = true;
      } else {
        this.video.pause();
        // this.video.controls = false;
      }
    });

    this.loadOnObserve();
  }

  pauseOtherVideos() {
    document.querySelectorAll("site-video").forEach((videoComponent) => {
      const video = videoComponent.querySelector("video");
      if (video !== this.video && !video.paused) {
        video.pause();
        // this.video.controls = false;
      }
    });
  }

  stopOnObserve() {
    if ("IntersectionObserver" in window) {
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!entry.isIntersecting) {
            video.pause();
            // this.video.controls = false;
          }
        });
      });

      videoObserver.observe(this.querySelector("video"));
    }
  }

  loadOnObserve() {
    if (!("IntersectionObserver" in window)) return;

    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const video = entry.target;

        const lazySources = video.querySelectorAll("source[data-src]");
        if (lazySources.length) {
          lazySources.forEach((source) => {
            source.src = source.dataset.src;
            source.removeAttribute("data-src");
          });
          video.load();
        }

        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }

        videoObserver.unobserve(video);
      });
    });

    videoObserver.observe(this.querySelector("video"));
  }
}

customElements.define("site-video", SiteVideo);

/*
  Klaviyo Signup
*/

class KlaviyoSignup extends HTMLElement {
  connectedCallback() {
    this.klaviyoListId = this.dataset.klaviyoListId;
    this.form = this.querySelector("form");
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.submitForm(e);
    });
  }

  async submitForm(e) {
    const email = this.form.querySelector('[type="email"]');
    const submit = this.form.querySelector('[type="submit"]');
    const submitText = submit.innerHTML;

    submit.innerHTML = "Submitting...";

    const body = {
      email: email.value,
      listId: this.klaviyoListId,
    };

    const config = {
      body: JSON.stringify(body),
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };

    if (this.klaviyoListId) {
      try {
        fetch(
          "https://hunni-serverless.vercel.app/api/notorious-foodie/subscribe",
          config
        )
          .then((res) => res.json())
          .then((data) => {
            this.form.reset();
            submit.innerHTML = submitText;
            const success = this.querySelector(".success");
            const message = this.querySelector(".message");
            if (success) {
              this.querySelector(".success").classList.remove("hidden");
            }
            if (message) {
              this.querySelector(".message").classList.add("hidden");
            }
          });
      } catch (e) {
        console.log(e.message);
      }
    }
  }
}

customElements.define("klaviyo-signup", KlaviyoSignup);

class TimerMessage extends HTMLElement {
  constructor() {
    super();
    this.timerInterval = null;
  }

  connectedCallback() {
    let timeRemaining = parseInt(sessionStorage.getItem("timeRemaining"), 10);
    const cartItems = document.querySelector(".cart-items").childNodes;

    if (timeRemaining && cartItems.length > 0) {
      this.initTimer();
      this.showMessage();
    } else {
      this.stopTimer();
    }
  }

  showMessage() {
    this.classList.remove("hidden");
    this.classList.add("flex");
  }

  hideMessage() {
    this.classList.add("hidden");
    this.classList.remove("flex");
  }

  initTimer() {
    const timerEl = this.querySelector(".timer");
    let timeRemaining = parseInt(sessionStorage.getItem("timeRemaining"), 10);

    if (!timeRemaining) {
      timeRemaining = 5 * 60;
    }

    const updateTimer = () => {
      let minutes = Math.floor(timeRemaining / 60);
      let seconds = timeRemaining % 60;
      seconds = seconds < 10 ? `0${seconds}` : seconds;
      timerEl.textContent = `${minutes}:${seconds}`;

      if (timeRemaining > 0) {
        timeRemaining--;
        sessionStorage.setItem("timeRemaining", timeRemaining);
      } else {
        this.removeAllCartItem();
        clearInterval(this.timerInterval);
        sessionStorage.removeItem("timeRemaining");
      }
    };

    updateTimer();
    this.timerInterval = setInterval(updateTimer, 1000);
  }

  stopTimer() {
    clearInterval(this.timerInterval);
  }

  removeAllCartItem() {
    const cartItems = document.querySelectorAll("cart-item");
    const siteCart = document.querySelector("site-cart");

    cartItems.forEach((item) => {
      siteCart.updateCart({ [item.dataset.id]: 0 });
    });
  }
}

customElements.define("timer-message", TimerMessage);
